import { Badge } from "@/components/ui/badge";
import { getBenchmark } from "@/lib/benchmarks";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Leaf,
  Users,
  Shield,
  BarChart3,
  Factory,
  Zap,
  Truck,
  Banknote,
  BadgeCheck,
  CheckCircle2,
  ShieldCheck,
  Map,
  Lock,
} from "lucide-react";

interface ReportData {
  assessment: {
    id: string;
    organizationName: string;
    reportingYear: number;
    currency: string;
    sector: string;
    locked: boolean;
  };
  carbonPerformance: {
    totalEmissionsTco2e: number;
    scope1Tco2e: number;
    scope2Tco2e: number;
    scope3Tco2e: number;
    intensityMetrics: {
      emissionsPerRevenue: number | null;
      emissionsPerEmployee: number | null;
      energyIntensity: number | null;
    };
    reportingPeriod: string;
  };
  esgStatus: {
    environmental: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
    social: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
    governance: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
  };
  trustIndicators: {
    completenessPercent: number;
    vsmeLevel: string;
    evidenceCount: number;
    dataQualityRating: number;
    boundaryStatement: string;
  };
  hotspots: Array<{
    source: string;
    tco2e: number;
    percentage: number;
  }>;
  recommendations: Array<{
    priority: string;
    title: string;
    description: string;
  }>;
}

interface ReportVisualizationProps {
  data: ReportData;
  showStatusAlert?: boolean;
}

const SCOPE_COLORS = {
  scope1: "#3b82f6",
  scope2: "#f59e0b",
  scope3: "#6366f1",
};

function ScopeDonutChart({
  scope1,
  scope2,
  scope3,
}: {
  scope1: number;
  scope2: number;
  scope3: number;
}) {
  const total = scope1 + scope2 + scope3;
  const allZero = total === 0;

  const data = [
    { name: "Scope 1", value: scope1, color: SCOPE_COLORS.scope1 },
    { name: "Scope 2", value: scope2, color: SCOPE_COLORS.scope2 },
    { name: "Scope 3", value: scope3, color: SCOPE_COLORS.scope3 },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col items-center justify-center">
      <p className="text-sm font-medium text-slate-500 mb-4 self-start">
        {"Scope split"}
      </p>
      {allZero ? (
        <div className="flex flex-col items-center gap-2">
          <div className="border-4 border-slate-200 rounded-full w-20 h-20" />
          <p className="text-xs text-slate-400">{"No emissions recorded"}</p>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                dataKey="value"
                paddingAngle={2}
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string) => [`${value.toFixed(3)} tCO2e`, name]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="text-xs text-slate-600">{entry.name}</span>
                <span className="text-xs font-bold text-slate-700">
                  {total > 0 ? `${((entry.value / total) * 100).toFixed(0)}%` : "—"}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function ReportVisualization({ data, showStatusAlert = true }: ReportVisualizationProps) {
  const { assessment, carbonPerformance, esgStatus, trustIndicators, hotspots, recommendations } = data;
  const benchmark = getBenchmark(assessment.sector);

  return (
    <div className="space-y-8">
      {/* Carbon Performance Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-slate-900">{"Carbon Performance"}</h2>
        </div>
        <p className="text-base text-slate-500">{`Baseline emissions footprint for ${carbonPerformance.reportingPeriod}`}</p>

        {/* Scope Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-5">
          {/* Total Emissions */}
          <div className="relative p-6 bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-start mb-8">
              <div className="h-8 px-2 bg-primary/10 rounded-full flex items-center justify-center">
                <BarChart3 className="h-4 w-4 text-primary" />
              </div>
              <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-bold text-slate-500">{"Baseline Year"}</span>
            </div>
            <p className="text-sm font-medium text-slate-500">{"Total Emissions"}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-black text-slate-900">{carbonPerformance.totalEmissionsTco2e.toFixed(3)}</span>
              <span className="text-sm text-slate-500">{"tCO2e"}</span>
            </div>
          </div>

          {/* Scope 1 */}
          <div className="relative p-6 bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-start mb-8">
              <div className="h-8 px-2 bg-blue-100 rounded-full flex items-center justify-center">
                <Factory className="h-4 w-4 text-blue-600" />
              </div>
              <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-bold text-slate-500">{"Baseline Year"}</span>
            </div>
            <p className="text-sm font-medium text-slate-500">{"Scope 1"}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-black text-slate-900">{carbonPerformance.scope1Tco2e?.toFixed(3) ?? 0}</span>
              <span className="text-sm text-slate-500">{"tCO2e from direct sources"}</span>
            </div>
          </div>

          {/* Scope 2 */}
          <div className="relative p-6 bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-start mb-8">
              <div className="h-8 px-2 bg-amber-100 rounded-full flex items-center justify-center">
                <Zap className="h-4 w-4 text-amber-600" />
              </div>
              <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-bold text-slate-500">{"Baseline Year"}</span>
            </div>
            <p className="text-sm font-medium text-slate-500">{"Scope 2"}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-black text-slate-900">{carbonPerformance.scope2Tco2e?.toFixed(3) ?? 0}</span>
              <span className="text-sm text-slate-500">{"tCO2e from purchased electricity"}</span>
            </div>
          </div>

          {/* Scope 3 */}
          <div className="relative p-6 bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-start mb-8">
              <div className="h-8 px-2 bg-indigo-100 rounded-full flex items-center justify-center">
                <Truck className="h-4 w-4 text-indigo-600" />
              </div>
              <span className="px-2 py-1 bg-slate-100 rounded-full text-xs font-bold text-slate-500">{"Baseline Year"}</span>
            </div>
            <p className="text-sm font-medium text-slate-500">{"Scope 3"}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-black text-slate-900">{carbonPerformance.scope3Tco2e?.toFixed(3) ?? 0}</span>
              <span className="text-sm text-slate-500">{"tCO2e from value chain"}</span>
            </div>
          </div>

          <ScopeDonutChart
            scope1={carbonPerformance.scope1Tco2e}
            scope2={carbonPerformance.scope2Tco2e}
            scope3={carbonPerformance.scope3Tco2e}
          />
        </div>

        {/* Intensity Metrics */}
        <p className="text-base text-slate-500 mt-8 mb-4">
          {`Intensity Metrics • ${carbonPerformance.reportingPeriod} • Currency: ${assessment.currency}`}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Efficiency Score */}
          <div className="p-6 bg-white rounded-xl border border-slate-200 flex items-center gap-4">
            <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Banknote className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{"Efficiency Score (Emissions/Turnover)"}</p>
              {carbonPerformance.intensityMetrics.emissionsPerRevenue !== null ? (
                <>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-xl font-bold text-slate-900">{carbonPerformance.intensityMetrics.emissionsPerRevenue.toFixed(3)}</span>
                    <span className="text-sm font-medium text-slate-500">{`tCO2e per 1,000 ${assessment.currency}`}</span>
                  </div>
                  {benchmark?.revenueIntensity != null && (
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                      <TrendingUp className="h-3 w-3 flex-shrink-0" />
                      {`Sector avg: ${benchmark.revenueIntensity.toFixed(3)} · ${benchmark.source}`}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-400 mt-0.5">{"Not available"}</p>
              )}
            </div>
          </div>

          {/* Emissions per Employee */}
          <div className="p-6 bg-white rounded-xl border border-slate-200 flex items-center gap-4">
            <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{"Per Employee"}</p>
              {carbonPerformance.intensityMetrics.emissionsPerEmployee !== null ? (
                <>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className="text-xl font-bold text-slate-900">{carbonPerformance.intensityMetrics.emissionsPerEmployee.toFixed(3)}</span>
                    <span className="text-sm font-medium text-slate-500">{"tCO2e per employee"}</span>
                  </div>
                  {benchmark?.employeeIntensity != null && (
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                      <TrendingUp className="h-3 w-3 flex-shrink-0" />
                      {`Sector avg: ${benchmark.employeeIntensity.toFixed(3)} · ${benchmark.source}`}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-400 mt-0.5">{"Not available"}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ESG Status Dashboards */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <BadgeCheck className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-slate-900">{"ESG Disclosures"}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Environmental Card */}
          <div className="relative p-5 bg-white rounded-xl border border-slate-200">
            <div className="flex justify-between items-start mb-1">
              <span className="px-2 py-1 bg-emerald-50 rounded-lg text-xs font-bold text-emerald-600 flex items-center gap-1.5">
                <Leaf className="h-3.5 w-3.5" />
                {"Environmental"}
              </span>
              <CheckCircle2 className={`h-5 w-5 ${esgStatus.environmental.completeness === 100 ? "text-emerald-500" : "text-slate-300"}`} />
            </div>
            <p className="text-sm text-slate-400 mb-6">{"Energy, water, waste metrics"}</p>
            <div className="space-y-1 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-400 uppercase">
                   {"Completeness"}:{" "}
                  {esgStatus.environmental.completeness === 100 ? "Complete" :
                    esgStatus.environmental.completeness > 0 ? "Partially Provided" : "Omitted"}
                </span>
                <span className="text-sm font-bold text-slate-900">{esgStatus.environmental.completeness}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${esgStatus.environmental.completeness}%` }} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {([
                ["carbon",      "Carbon"],
                ["pollution",   "Pollution"],
                ["water",       "Water"],
                ["waste",       "Waste"],
                ["biodiversity","Biodiversity"],
              ] as const).map(([id, label]) => (
                <span key={id} className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Social Card */}
          <div className="relative p-5 bg-white rounded-xl border border-slate-200">
            <div className="flex justify-between items-start mb-1">
              <span className="px-2 py-1 bg-blue-500/10 rounded-lg text-xs font-bold text-blue-500 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {"Social"}
              </span>
              <CheckCircle2 className={`h-5 w-5 ${esgStatus.social.completeness === 100 ? "text-blue-500" : "text-slate-300"}`} />
            </div>
            <p className="text-sm text-slate-400 mb-6">{"Workforce, health & safety"}</p>
            <div className="space-y-1 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-400 uppercase">
                   {"Completeness"}:{" "}
                  {esgStatus.social.completeness === 100 ? "Complete" :
                    esgStatus.social.completeness > 0 ? "Partially Provided" : "Omitted"}
                </span>
                <span className="text-sm font-bold text-slate-900">{esgStatus.social.completeness}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${esgStatus.social.completeness}%` }} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {([
                ["workforce",         "Workforce"],
                ["diversityInclusion","Diversity & Inclusion"],
                ["humanRights",       "Human Rights"],
                ["community",         "Community"],
              ] as const).map(([id, label]) => (
                <span key={id} className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Governance Card */}
          <div className="relative p-5 bg-white rounded-xl border border-primary/20">
            <div className="flex justify-between items-start mb-1">
              <span className="px-2 py-1 bg-primary/10 rounded-lg text-xs font-bold text-primary flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" />
                {"Governance"}
              </span>
              <CheckCircle2 className={`h-5 w-5 ${esgStatus.governance.completeness === 100 ? "text-primary" : "text-slate-300"}`} />
            </div>
            <p className="text-sm text-slate-400 mb-6">{"Ethics, transparency, leadership"}</p>
            <div className="space-y-1 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-400 uppercase">
                   {"Completeness"}:{" "}
                  {esgStatus.governance.completeness === 100 ? "Complete" :
                    esgStatus.governance.completeness > 0 ? "Partially Provided" : "Omitted"}
                </span>
                <span className="text-sm font-bold text-slate-900">{esgStatus.governance.completeness}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${esgStatus.governance.completeness}%` }} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {([
                ["ethics",         "Ethics"],
                ["transparency",   "Transparency"],
                ["leadership",     "Leadership"],
                ["accountability", "Accountability"],
              ] as const).map(([id, label]) => (
                <span key={id} className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Trust & Transparency Indicators */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-slate-900">{"Trust & Transparency Indicators"}</h2>
        </div>
        <p className="text-base text-slate-500 mb-4">{"Data quality and credibility signals"}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Data Completeness */}
          <div className="p-5 bg-white rounded-3xl border border-slate-200 flex flex-col gap-3">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-bold text-slate-900">{"Data Completeness"}</p>
              <p className="text-2xl font-black text-slate-900">{trustIndicators.completenessPercent}%</p>
            </div>
            <span className="px-2 py-1 bg-emerald-50 rounded-lg text-xs font-bold text-emerald-600 w-fit">
              {trustIndicators.vsmeLevel}
            </span>
          </div>

          {/* Evidence Quality */}
          <div className="p-5 bg-white rounded-3xl border border-slate-200 flex flex-col gap-3">
            <BarChart3 className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-bold text-slate-900">{"Evidence Quality"}</p>
              {assessment.locked ? (
                <span className="mt-1 inline-flex px-2 py-1 bg-emerald-50 rounded-lg text-xs font-bold text-emerald-700">
                  {"System Verified ✓"}
                </span>
              ) : (
                <>
                  <div className="flex items-baseline">
                    <span className="text-2xl font-black text-slate-900">{trustIndicators.dataQualityRating.toFixed(1)}</span>
                    <span className="text-sm text-slate-400">/5.0</span>
                  </div>
                  <p className="text-sm text-slate-400">
                    {`${trustIndicators.evidenceCount} documents uploaded`}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Reporting Boundary */}
          <div className="p-5 bg-white rounded-3xl border border-slate-200 flex flex-col gap-3">
            <Map className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-bold text-slate-900">{"Reporting Boundary"}</p>
              <p className="text-lg font-bold text-slate-900">{trustIndicators.boundaryStatement}</p>
            </div>
          </div>

          {/* Assessment Status */}
          <div className="p-5 bg-white rounded-3xl border border-slate-200 flex flex-col gap-3">
            <Lock className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-bold text-slate-900">{"Assessment Status"}</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${assessment.locked ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="text-lg font-bold text-slate-900">
                  {assessment.locked ? "Locked (Final)" : "Draft"}
                </span>
              </div>
            </div>
            <p className="text-sm text-slate-400">
              {`Snapshot: ${assessment.id.substring(0, 8)} • FY ${assessment.reportingYear}`}
            </p>
          </div>
        </div>
      </div>

      {/* Actionable Insights */}
      {(hotspots.length > 0 || recommendations.length > 0) && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-bold text-slate-900">{"Actionable Insights"}</h2>
          </div>
          <p className="text-base text-slate-500 mb-4">{"Hotspot analysis and improvement recommendations"}</p>

          {/* Top Drivers */}
          {hotspots.length > 0 && (
            <div className="p-6 bg-white rounded-xl border border-slate-200">
              <div className="flex items-center gap-2 mb-6">
                <AlertTriangle className="h-4 w-4 text-primary" />
                <h3 className="text-lg font-bold text-slate-900">{"Top Emission Drivers"}</h3>
              </div>
              <div className="flex flex-col gap-2.5">
                {hotspots.map((hotspot, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 py-1 bg-slate-100 rounded-lg flex justify-center items-center">
                          <span className="text-xs font-bold text-slate-500">{index + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-900">{hotspot.source}</p>
                          <p className="text-xs text-slate-500">
                            {`${hotspot.percentage}% of total emissions`}
                          </p>
                          <div className="h-1.5 bg-slate-100 rounded-full mt-1.5">
                            <div
                              className="h-full bg-primary/60 rounded-full transition-all duration-500"
                              style={{ width: `${hotspot.percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-sm font-bold text-slate-900">{hotspot.tco2e.toFixed(3)}</span>
                        <span className="text-[10px] font-bold text-slate-400">{"tCO2e"}</span>
                      </div>
                    </div>
                    {index < hotspots.length - 1 && (
                      <div className="border-t border-black/10 mt-2.5" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="p-6 bg-white rounded-xl border border-slate-200 mt-4">
              <h3 className="text-lg font-bold text-slate-900 mb-4">{"What's Next: Recommended Actions"}</h3>
              <div className="space-y-3">
                {recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${rec.priority === 'high' ? 'border-l-red-600 bg-red-50/50' : 'border-l-amber-500 bg-amber-50/50'}`}
                  >
                    <div className="flex items-start gap-3">
                      <Badge
                        variant={rec.priority === 'high' ? 'destructive' : 'secondary'}
                        className="mt-0.5"
                      >
                        {rec.priority.toUpperCase()}
                      </Badge>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 mb-1">{rec.title}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed">{rec.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

